<template>
    <div class="col-12 nopads">
        <div v-if="tableRows.length > 0">
            <vue3-datatable
                :rows="tableRows"
                :columns="fields"
                :pagination="false"
                :sticky-header="true"
                :height="tableRows.length > 10 ? '60vh' : '100%'"
                :sortable="true"
                skin="bh-table-compact"
                row-class="routa-table-row"
                cell-class="routa-table-cell"
            />
        </div>
    </div>
</template>

<script>
import Vue3Datatable from '@bhplugin/vue3-datatable'

function computeTableRows(results, t) {
    const names = {}
    const distances = {}
    const roadDistances = {}
    const durationSecs = {}
    const allocationMins = {}
    const co2Raw = {}
    const costs = {}

    results.forEach(item => {
        const key = (item.order && item.order.task_type) ? item.order.task_type.id : 'no_order'

        if (!Object.prototype.hasOwnProperty.call(distances, key)) {
            names[key] = item.order && item.order.task_type
                ? item.order.task_type.name
                : t('daily_summary.no_order')
            distances[key] = 0
            roadDistances[key] = 0
            durationSecs[key] = 0
            allocationMins[key] = 0
            co2Raw[key] = 0
            costs[key] = 0
        }

        if (item.order && item.order.task_type) {
            names[key] = item.order.task_type.name
        }

        const tripDistance = (Number(item.work_distance) || 0) + (Number(item.resettle_distance) || 0)
        distances[key] += tripDistance
        roadDistances[key] += Number(item.covered_road_distance) || 0
        durationSecs[key] += Number(item.duration) || 0
        allocationMins[key] += Number(item.vehicle_allocation_time) || 0
        costs[key] += Number(item.cost) || 0

        const co2 = Number(item.co2_emissions)
        if (co2 > 0) {
            co2Raw[key] += co2 * tripDistance
        }
    })

    return Object.keys(distances).map(key => {
        const durationHours = durationSecs[key] / 3600
        const avgSpeed = durationSecs[key] > 0
            ? distances[key] / (durationSecs[key] / 3600)
            : 0
        return {
            workTypeName: names[key],
            distance: distances[key],
            roadDistance: roadDistances[key],
            durationHours,
            allocationHours: allocationMins[key] / 60,
            avgSpeed,
            co2Kg: co2Raw[key] / 1000,
            cost: costs[key],
        }
    })
}

export default {
    name: 'ContractTripSummaryTable',
    components: { Vue3Datatable },
    props: {
        results: {
            type: Array,
            default() {
                return []
            }
        }
    },
    computed: {
        fields() {
            const currency = import.meta.env.VITE_APP_CURRENCY || '€'
            return [
                { field: 'workTypeName', title: this.$t('trip_summary.task_type'), sort: true, headerClass: 'routa-table-header' },
                { field: 'cost', title: this.$t('daily_summary.vehicle_costs') + ' (' + currency + ')', sort: true, type: 'number', headerClass: 'routa-table-header' },
                { field: 'distance', title: this.$t('trip_list.trip_length') + ' (km)', sort: true, type: 'number', headerClass: 'routa-table-header' },
                { field: 'roadDistance', title: this.$t('trip_list.road_distance') + ' (km)', sort: true, type: 'number', headerClass: 'routa-table-header' },
                { field: 'durationHours', title: this.$t('trip_list.duration') + ' (h)', sort: true, type: 'number', headerClass: 'routa-table-header' },
                { field: 'allocationHours', title: this.$t('trip_list.vehicle_allocation') + ' (h)', sort: true, type: 'number', headerClass: 'routa-table-header' },
                { field: 'avgSpeed', title: this.$t('trip_list.avg_speed') + ' (km/h)', sort: true, type: 'number', headerClass: 'routa-table-header' },
                { field: 'co2Kg', title: this.$t('co2_emissions.title') + ' (kg)', sort: true, type: 'number', headerClass: 'routa-table-header' },
            ]
        },
        tableRows() {
            const rows = computeTableRows(this.results, (key) => this.$t(key))

            // Totals row
            if (rows.length === 0) return rows

            const totalDistance = rows.reduce((s, r) => s + r.distance, 0)
            const totalRoadDistance = rows.reduce((s, r) => s + r.roadDistance, 0)
            const totalDurationHours = rows.reduce((s, r) => s + r.durationHours, 0)
            const totalAllocationHours = rows.reduce((s, r) => s + r.allocationHours, 0)
            const totalCo2Kg = rows.reduce((s, r) => s + r.co2Kg, 0)
            const totalDurationSeconds = totalDurationHours * 3600
            const avgSpeed = totalDurationSeconds > 0 ? totalDistance / (totalDurationSeconds / 3600) : 0

            const formattedRows = rows.map(r => ({
                workTypeName: r.workTypeName,
                distance: parseFloat(r.distance.toFixed(2)),
                roadDistance: parseFloat(r.roadDistance.toFixed(2)),
                durationHours: parseFloat(r.durationHours.toFixed(2)),
                allocationHours: parseFloat(r.allocationHours.toFixed(2)),
                avgSpeed: parseFloat(r.avgSpeed.toFixed(1)),
                co2Kg: parseFloat(r.co2Kg.toFixed(1)),
                cost: parseFloat(r.cost.toFixed(2)),
            }))

            const totalCost = rows.reduce((s, r) => s + r.cost, 0)

            formattedRows.push({
                workTypeName: this.$t('common.total'),
                distance: parseFloat(totalDistance.toFixed(2)),
                roadDistance: parseFloat(totalRoadDistance.toFixed(2)),
                durationHours: parseFloat(totalDurationHours.toFixed(2)),
                allocationHours: parseFloat(totalAllocationHours.toFixed(2)),
                avgSpeed: parseFloat(avgSpeed.toFixed(1)),
                co2Kg: parseFloat(totalCo2Kg.toFixed(1)),
                cost: parseFloat(totalCost.toFixed(2)),
            })

            return formattedRows
        }
    }
}
</script>
